import { useCallback, useState } from "react";
import Comments from "../Comments";
import ProImageSlider from "./ProImageSlider";
import { toggleProLike, shareProBlog, reportProComment, formatDate } from "../../services/proSystemApi";
import { formatDuration } from "../../services/proApi";
import "../Comments.css";

// =====================================================
// ONE PRO BLOG  (Steps 8, 12, 13, and Part B items 3 and 4)
//
// ── THE RENDER ORDER (Part B item 3) ──
// Title → Content → Image → Video. Top to bottom, in that order, with nothing
// interleaved. The same order the free blog pages already use, the same order
// the composer collects them in, and the same order the share preview shows —
// so a blog reads identically wherever it is opened.
//
// ── LOCKED vs UNLOCKED (Step 8) ──
// A locked blog does not render less; it HAS less. The server never sent the
// body, the image URLs or the video URL for a blog this reader has not paid
// for, so there is nothing here to accidentally reveal. What arrives is a
// title, an author and the counts — and that is what is shown, above a button
// to unlock the creator.
//
// ── ENGAGEMENT (Step 12) ──
// Like, Comment and Share, reusing the app's EXISTING free components: the
// comment thread below is the very same <Comments/> the free blog pages use,
// with no modification, because Pro comments are stored in the same Comment
// collection keyed by the post id.
// =====================================================

// Roughly the point at which the body outgrows the four-line clamp. Used only
// to decide whether a Read more toggle is worth showing — the clamp itself is
// done in CSS, which is what actually controls the height.
const LONG_CONTENT_CHARS = 200;

function ProBlogCard({
  blog,
  currentUsername,
  currentUserPic,
  onUnlock,
  onOpen,
  onEdit,
  unlockPriceRupees = 19,
  compact = false,
}) {
  const [expanded, setExpanded] = useState(false);

  const isLongContent = (blog.content || "").length > LONG_CONTENT_CHARS;
  const [liked, setLiked] = useState(Boolean(blog.likedByMe));
  const [likeCount, setLikeCount] = useState(blog.likeCount || 0);
  const [shareCount, setShareCount] = useState(blog.shareCount || 0);
  const [commentCount, setCommentCount] = useState(blog.commentCount || 0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const flash = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }, []);

  // ── LIKE ──
  // The count is taken from the SERVER's reply, not incremented locally, so a
  // rejected like (expired access, for instance) never leaves a wrong number
  // on screen.
  const onLike = async () => {
    if (busy) return;
    setBusy(true);

    try {
      const res = await toggleProLike(blog._id);
      setLiked(Boolean(res.liked));
      setLikeCount(res.likeCount || 0);
    } catch (err) {
      flash(err?.response?.data?.message || "Could not update like");
    } finally {
      setBusy(false);
    }
  };

  // ── SHARE (Step 14) ──
  // The link shared is the SECURE PREVIEW route. Opening it runs the same
  // paywall check against whoever opened it, so a shared Pro blog can never
  // leak to someone who has not paid.
  const onShare = async () => {
    if (busy) return;
    setBusy(true);

    try {
      const res = await shareProBlog(blog._id);
      setShareCount(res.shareCount || 0);

      const url = `${window.location.origin}${res.sharePath}`;

      if (navigator.share) {
        await navigator.share({ title: blog.title || "Pro blog", url });
      } else {
        await navigator.clipboard.writeText(url);
        flash("Link copied");
      }
    } catch (err) {
      // A cancelled native share throws too — that is not an error worth
      // shouting about.
      if (err?.name !== "AbortError") {
        flash(err?.response?.data?.message || "Could not share this blog");
      }
    } finally {
      setBusy(false);
    }
  };

  // Told to the server after a comment is posted through the free comment
  // endpoints, so the creator gets a Pro notification (Step 20). The server
  // re-checks that the comment really exists before it writes one.
  const onCommentPosted = useCallback(async () => {
    try {
      const res = await reportProComment(blog._id);
      if (typeof res?.commentCount === "number") setCommentCount(res.commentCount);
    } catch (err) {
      // A missed notification must never break the comment that was posted.
    }
  }, [blog._id]);

  // =====================================================
  // LOCKED  (Step 8)
  // =====================================================
  if (blog.locked) {
    return (
      <article className="pro-card pro-blog pro-blog-locked">
        <header className="pro-blog-head">
          <h3 className="pro-blog-title">{blog.title || "Untitled"}</h3>
          <span className="pro-blog-date">{formatDate(blog.createdAt)}</span>
        </header>

        <div className="pro-lock-body">
          <span className="pro-lock-icon" aria-hidden="true">🔒</span>

          <p className="pro-lock-text">
            This Pro blog is locked. Unlock <strong>{blog.author}</strong>&rsquo;s Pro
            blogs for <strong>₹{unlockPriceRupees}</strong> to read it.
          </p>

          <p className="pro-lock-meta">
            {blog.imageCount > 0 && (
              <span>
                🖼 {blog.imageCount} image{blog.imageCount === 1 ? "" : "s"}
              </span>
            )}
            {blog.hasVideo && <span>🎥 Video</span>}
            <span>❤️ {blog.likeCount || 0}</span>
          </p>

          {onUnlock && (
            <button type="button" className="pro-btn pro-btn-primary" onClick={onUnlock}>
              🔓 Pay ₹{unlockPriceRupees} to unlock
            </button>
          )}
        </div>
      </article>
    );
  }

  // =====================================================
  // UNLOCKED
  // =====================================================
  return (
    <article className="pro-card pro-blog">
      {toast && <div className="pro-inline-toast">{toast}</div>}

      <header className="pro-blog-head">
        <div className="pro-blog-author">
          {blog.authorImage ? (
            <img src={blog.authorImage} alt={blog.author} className="pro-blog-avatar" />
          ) : (
            <span className="pro-blog-avatar pro-blog-avatar-fallback">
              {(blog.author || "?").charAt(0).toUpperCase()}
            </span>
          )}
          <div className="pro-blog-author-meta">
            <span className="pro-blog-author-name">{blog.author}</span>
            <span className="pro-blog-date">{formatDate(blog.createdAt)}</span>
          </div>
        </div>

        {blog.access === "preview" && (
          <span className="pro-chip pro-chip-free">Free preview</span>
        )}
      </header>

      {/* ── 1. TITLE ── */}
      {blog.title && (
        <h3
          className={`pro-blog-title${onOpen ? " pro-blog-title-link" : ""}`}
          onClick={onOpen}
          role={onOpen ? "button" : undefined}
          tabIndex={onOpen ? 0 : undefined}
          onKeyDown={onOpen ? (e) => e.key === "Enter" && onOpen() : undefined}
        >
          {blog.title}
        </h3>
      )}

      {/* ── 2. CONTENT ──
          In a LIST the body is clamped so ten posts do not become ten screens,
          but it was clamped with no way to open it — the text just stopped at
          "..." and the reader was stuck. A Read more / Read less toggle now
          expands it in place, the same way the free blog cards behave. */}
      {blog.content && (
        <>
          <p
            className={`pro-blog-content${
              compact && isLongContent && !expanded ? " pro-blog-content-clamp" : ""
            }`}
          >
            {blog.content}
          </p>

          {compact && isLongContent && (
            <button
              type="button"
              className="pro-read-toggle"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
            >
              {expanded ? "Read less" : "Read more"}
            </button>
          )}
        </>
      )}

      {/* ── 3. IMAGE ──
          One image at a time, swipeable, with dots. A single image renders
          without any slider controls. */}
      {Array.isArray(blog.images) && blog.images.length > 0 && (
        <div className="pro-blog-media">
          <ProImageSlider
            images={blog.images}
            alt={blog.title || "Pro blog image"}
            aspectRatio={blog.aspectRatio}
          />
        </div>
      )}

      {/* ── 4. VIDEO ──
          BELOW the image slider, as its own element — never a slide inside it. */}
      {blog.video && (
        <div className="pro-blog-media pro-blog-video">
          <video src={blog.video} controls preload="metadata" playsInline />
          {blog.videoDuration > 0 && (
            <span className="pro-blog-duration">{formatDuration(blog.videoDuration)}</span>
          )}
        </div>
      )}

      {/* ── ENGAGEMENT (Step 12) — the three plain counts of Step 13 ── */}
      <footer className="pro-blog-actions">
        <button
          type="button"
          className={`pro-action${liked ? " pro-action-on" : ""}`}
          onClick={onLike}
          disabled={busy}
          aria-pressed={liked}
        >
          <span aria-hidden="true">{liked ? "❤️" : "🤍"}</span>
          <span>{likeCount}</span>
        </button>

        {!blog.hideComments && (
          <button
            type="button"
            className={`pro-action${commentsOpen ? " pro-action-on" : ""}`}
            onClick={() => setCommentsOpen((open) => !open)}
          >
            <span aria-hidden="true">💬</span>
            <span>{commentCount}</span>
          </button>
        )}

        {blog.allowShare !== false && (
          <button type="button" className="pro-action" onClick={onShare} disabled={busy}>
            <span aria-hidden="true">🔗</span>
            <span>{shareCount}</span>
          </button>
        )}

        {/* Open and Edit belong to the card, so they sit INSIDE its action row,
            pushed to the right. Edit used to hang below the card on its own,
            which read as if it belonged to the next post rather than this one. */}
        {(onOpen || onEdit) && (
          <span className="pro-blog-actions-right">
            {onOpen && (
              <button type="button" className="pro-btn pro-btn-ghost pro-btn-sm" onClick={onOpen}>
                Open
              </button>
            )}

            {onEdit && (
              <button type="button" className="pro-btn pro-btn-ghost pro-btn-sm" onClick={onEdit}>
                ✏️ Edit
              </button>
            )}
          </span>
        )}
      </footer>

      {/* The app's EXISTING comment thread, unchanged — Step 12 asks for the
          free UI components to be reused, and this is literally the same one
          the free blog pages render. */}
      {!blog.hideComments && commentsOpen && (
        <div className="pro-blog-comments">
          <Comments
            blogId={blog._id}
            blogAuthor={blog.author}
            currentUsername={currentUsername}
            currentUserPic={currentUserPic}
            isOpen={commentsOpen}
            onCommentPosted={onCommentPosted}
          />
        </div>
      )}
    </article>
  );
}

export default ProBlogCard;
